import { CreditCard, MoreVertical, Trash2, Edit2, Star } from "lucide-react";
import { useState } from "react";

export interface PaymentCard {
  id: string;
  type: "credit" | "debit";
  brand: "visa" | "mastercard" | "elo" | "amex";
  lastFourDigits: string;
  cardholderName: string;
  expiryDate: string;
  isDefault: boolean;
}

interface PaymentCardItemProps {
  card: PaymentCard;
  onEdit: (card: PaymentCard) => void;
  onDelete: (id: string) => void;
  onSetDefault: (id: string) => void;
}

export function PaymentCardItem({
  card,
  onEdit,
  onDelete,
  onSetDefault,
}: PaymentCardItemProps) {
  const [showMenu, setShowMenu] = useState(false);

  const getBrandColor = () => {
    switch (card.brand) {
      case "visa":
        return "bg-blue-600";
      case "mastercard":
        return "bg-red-600";
      case "elo":
        return "bg-yellow-600";
      case "amex":
        return "bg-green-600";
      default:
        return "bg-gray-600";
    }
  };

  const getCardTypeName = () => {
    return card.type === "credit" ? "Crédito" : "Débito";
  };

  return (
    <div className="relative bg-card rounded-xl shadow-sm border border-border p-4">
      <div className="flex items-start gap-4">
        <div
          className={`w-12 h-12 ${getBrandColor()} rounded-lg flex items-center justify-center flex-shrink-0`}
        >
          <CreditCard className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-medium">
              {card.brand.toUpperCase()} •••• {card.lastFourDigits}
            </h3>
            {card.isDefault && (
              <div className="flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded">
                <Star className="w-3 h-3 fill-current" />
                Padrão
              </div>
            )}
          </div>
          <p className="text-sm text-muted-foreground mb-1">
            {card.cardholderName}
          </p>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span>{getCardTypeName()}</span>
            <span>•</span>
            <span>Válido até {card.expiryDate}</span>
          </div>
        </div>
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 hover:bg-accent rounded-lg transition-colors"
            aria-label="Opções"
          >
            <MoreVertical className="w-5 h-5" />
          </button>

          {showMenu && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowMenu(false)}
              />
              <div className="absolute right-0 top-full mt-2 w-48 bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden">
                {!card.isDefault && (
                  <button
                    onClick={() => {
                      onSetDefault(card.id);
                      setShowMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-accent transition-colors text-left"
                  >
                    <Star className="w-4 h-4" />
                    <span className="text-sm">Tornar padrão</span>
                  </button>
                )}
                <button
                  onClick={() => {
                    onEdit(card);
                    setShowMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-accent transition-colors text-left"
                >
                  <Edit2 className="w-4 h-4" />
                  <span className="text-sm">Editar</span>
                </button>
                <button
                  onClick={() => {
                    onDelete(card.id);
                    setShowMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-accent transition-colors text-left text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="text-sm">Remover</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
